import { useState, useEffect, useCallback } from "react";
import { getToken } from "firebase/messaging";
import { setFcmRegistrationToken } from "@/requests/gen/react-query/user";
import { messaging } from "@/auth/firebase";
import { useAuth } from "@/auth/use-auth";
import { UserRoleEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import ConfirmModal from "@/components/modals/confirm-modal";

// localStorage helper functions
const getNotificationPreference = (userId: string): boolean | null => {
  const key = `notification-preference-${userId}`;
  const stored = localStorage.getItem(key);
  return stored ? JSON.parse(stored) : null;
};

const setNotificationPreference = (
  userId: string,
  preference: boolean,
): void => {
  const key = `notification-preference-${userId}`;
  localStorage.setItem(key, JSON.stringify(preference));
};

const notificationRoles = new Set<UserRoleEnum>([
  UserRoleEnum.admin,
  UserRoleEnum.patient_advocate,
  UserRoleEnum.fulfillment_team,
]);

export function NotificationPermission() {
  const { authUser } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const requireNotifications = authUser
    ? notificationRoles.has(authUser.role)
    : false;

  // Update FCM token when permission is granted
  const updateFcmToken = useCallback(async () => {
    if (Notification.permission !== "granted" || !messaging) {
      return;
    }

    try {
      const currentToken = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_PUBLIC_FIREBASE_VAPID_KEY,
      });

      if (currentToken) {
        await setFcmRegistrationToken(currentToken);
      }
    } catch (err) {
      console.error("An error occurred while retrieving token. ", err);
    }
  }, []);

  // Request notification permission
  const requestPermission = () => {
    if (Notification.permission === "default") {
      setIsDialogOpen(true);
    }
  };

  // Handle permission request result
  const handlePermissionResult = useCallback(
    async (result: NotificationPermission) => {
      setIsDialogOpen(false);

      if (authUser?.user_id) {
        // Store preference in localStorage for non-required roles
        if (!requireNotifications) {
          setNotificationPreference(authUser.user_id, result === "granted");
        }

        if (result === "granted") {
          await updateFcmToken();
        }
      }
    },
    [authUser?.user_id, requireNotifications, updateFcmToken],
  );

  // Handle allow button click
  const handleAllow = async () => {
    try {
      // This makes the request to the browser
      const result = await Notification.requestPermission();
      await handlePermissionResult(result);
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      setIsDialogOpen(false);
    }
  };

  // Auto-request permission when user logs in, if not already granted or denied
  useEffect(() => {
    if (authUser?.user_id) {
      const currentPermission = Notification.permission;

      if (currentPermission === "granted") {
        updateFcmToken();
        return;
      } else if (currentPermission === "default") {
        const storedPreference = getNotificationPreference(authUser.user_id);
        if (requireNotifications || storedPreference === null) {
          // If notifications are required or no preference is stored, ask for permission
          requestPermission();
        }
      }
    }
  }, [authUser?.user_id, requireNotifications, updateFcmToken]);

  return (
    <ConfirmModal
      isOpen={isDialogOpen}
      title="Enable Notifications"
      text="Stay updated with important notifications about your orders, tasks, and messages. You can change this setting anytime in your browser preferences."
      subtext={
        requireNotifications
          ? "It is required for Legrande employees to allow notifications."
          : undefined
      }
      img="notification"
      onCancel={() => handlePermissionResult("denied")}
      onConfirm={handleAllow}
      cancelText="Not Now"
      confirmText="Allow Notifications"
    />
  );
}
